import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Copy, Check } from "lucide-react";
import { MaskLines } from "./Reveal";
import { Link } from "react-router-dom";
import { MAILTO } from "./Nav";
import { Logo } from "./Logo";

const EMAIL = "info@clevantatechnologies.com";
export const PHONE = "+91 7981447040";
export const PHONE_HREF = "tel:+917981447040";
export const ADDRESS = "Plot No.1177, Lotus Pond Road, Near Omega Hospital M.L.A Colony, Road No.12, Banjara Hills # 500034, Hyderabad, Telangana 500096";
export const MAPS_URL = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`Clevanta Technologies, ${ADDRESS}`)}`;

export const Contact = () => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(EMAIL); } catch (e) { return; }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <section data-testid="contact-cta-section" className="px-4 pb-6 sm:px-6">
      <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[32px] bg-coral px-6 py-20 text-ink sm:px-12 md:py-28">
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-amber blur-[90px]" />
        <div className="relative">
          <p className="mb-6 font-mono text-xs uppercase tracking-[0.2em]">06 — Let's build</p>
          <h2 className="font-display text-5xl leading-[0.95] tracking-tight sm:text-7xl lg:text-[8rem]">
            <MaskLines inView lines={["Have an AI idea?", <em>Let's make it real.</em>]} />
          </h2>
          <div className="mt-12 flex flex-col gap-4 sm:flex-row sm:items-center">
            <motion.a whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} href={MAILTO} data-testid="contact-mailto-link" className="group inline-flex items-center justify-between gap-4 rounded-full bg-ink py-2 pl-7 pr-2 text-base font-semibold text-[#FBF9F5] sm:text-lg">
              <span className="text-[13px] sm:text-lg">{EMAIL}</span>
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#FBF9F5] text-ink transition-transform duration-500 group-hover:rotate-45"><ArrowUpRight className="h-5 w-5" /></span>
            </motion.a>
            <button onClick={copy} data-testid="contact-copy-email-btn" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-ink px-6 py-3.5 font-semibold transition-colors hover:bg-ink hover:text-[#FBF9F5]">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copied" : "Copy email"}
            </button>
            <Link to="/contact" data-testid="contact-page-link" className="link-underline self-start font-semibold sm:self-center">Or use the contact form</Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export const Footer = () => (
  <footer data-testid="footer-section" className="px-4 pb-4 sm:px-6">
    <div className="mx-auto max-w-7xl rounded-[32px] bg-ink px-6 pb-8 pt-16 text-[#FBF9F5] sm:px-12">
      <div className="flex flex-col justify-between gap-10 md:flex-row">
        <div className="max-w-sm">
          <Logo dark testId="footer-logo" />
          <p className="mt-5 text-base leading-relaxed text-white/75">Clevanta Technologies — generative AI, data and machine learning services, engineered for production.</p>
        </div>
        <div className="grid grid-cols-1 gap-10 text-sm sm:grid-cols-2">
          <div className="flex flex-col gap-3">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/60">Explore</span>
            {[["services", "Services"], ["process", "Process"], ["cases", "Use cases"], ["faq", "FAQ"]].map(([id, l]) => (
              <Link key={id} to={`/#${id}`} data-testid={`footer-link-${id}`} className="text-white/85 hover:text-amber">{l}</Link>
            ))}
            <Link to="/contact" data-testid="footer-link-contact" className="text-white/85 hover:text-amber">Contact</Link>
          </div>
          <div className="flex flex-col gap-3">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/60">Contact</span>
            <a href={MAILTO} data-testid="footer-email-link" className="break-all text-white/85 hover:text-amber">{EMAIL}</a>
            <a href={PHONE_HREF} data-testid="footer-phone-link" className="text-white/85 hover:text-amber">{PHONE}</a>
            <address data-testid="footer-address" className="max-w-xs not-italic leading-relaxed text-white/75">{ADDRESS}</address>
          </div>
        </div>
      </div>
      <p className="mt-16 select-none font-display text-[22vw] leading-[0.8] tracking-tight text-white/10 md:text-[15vw]">Clevanta</p>
      <div className="mt-6 flex flex-col justify-between gap-2 border-t border-white/15 pt-6 text-xs text-white/60 sm:flex-row">
        <span>© {new Date().getFullYear()} Clevanta Technologies. All rights reserved.</span>
        <a href="#top" data-testid="footer-back-to-top" className="hover:text-amber">Back to top ↑</a>
      </div>
    </div>
  </footer>
);
